import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import LoadingScreen from 'src/components/LoadingScreen';
import DPXWrapper from 'src/components/DPXWrapper';
import { MainLayout, DashboardLayout } from 'src/layouts';

import {
    AccountRoutes,
    AdCampaignsRoutes,
    AuthRoutes,
    BDCRoutes,
    CalendarsRoutes,
    CatalogRoutes,
    ChartsRoutes,
    ChatBotRoutes,
    ConversationsRoutes,
    DashboardsRoutes,
    DocumentsRoutes,
    DrilldownsRoutes,
    FinancialInstitutionsRoutes,
    GroupsRoutes,
    InvestmentChartRoutes,
    InternalReportsRoutes,
    LeadsRoutes,
    MaintenancePackagesRoutes,
    MakesRoutes,
    ManagementRoutes,
    RecordingTestRoutes,
    RecordingsRoutes,
    ReportsRoutes,
    SalesRoutes,
    ServiceClientsRoutes,
    ContactsRoutes,
    ShowroomRoutes,
    SourcesRoutes,
    StatusRoutes,
    StoresRoutes,
    StoreGroupsRoutes,
    SubstatusRoutes,
    TemplatesTwilioRoutes,
    TicketsRoutes,
    UsersRoutes,
    UtilsRoutes,
    VehiclesRoutes,
    EvaluationsRoutes,
    MetaAdsRoutes,
    ImpersonationRoutes,
    ImpersonationPublicRoutes,
    AssistantPermissionsRoutes,
} from 'src/routing';
import RolesGuard from './components/RolesGuard';

import { EmailMarketingView } from './views/plans';
import { ConversationsView } from './views/OmniChannel/ConversationsView';

export const renderRoutes = (routes = []) => (
    <Suspense fallback={<LoadingScreen />}>
        <Switch>
            {routes.map((route, key) => {
                const Guard = route.guard || Fragment;
                const Layout = route.layout || Fragment;
                const Component = route.component;

                const { roles, path, exact, routes } = route;

                const Guards = route.guard
                    ? Array.isArray(route.guard)
                        ? route.guard
                        : [route.guard]
                    : [Fragment];

                return (
                    <Route
                        key={key}
                        path={path}
                        exact={exact}
                        render={(props) => {
                            let content = (
                                <Layout>
                                    {routes ? (
                                        renderRoutes(routes)
                                    ) : (
                                        <DPXWrapper {...props}>
                                            <Component />
                                        </DPXWrapper>
                                    )}
                                </Layout>
                            );

                            Guards.forEach((GuardComponent) => {
                                content = (
                                    <GuardComponent
                                        {...(GuardComponent !== Fragment && {
                                            roles,
                                        })}
                                    >
                                        {content}
                                    </GuardComponent>
                                );
                            });

                            return content;
                        }}
                    />
                );
            })}
        </Switch>
    </Suspense>
);

const routes = [
    ...ImpersonationPublicRoutes,
    {
        path: '/privacy-notice',
        layout: MainLayout,
        exact: true,
        component: lazy(() => import('src/views/privacyNotice')),
    },
    {
        path: '/email-marketing/suscriptions',
        layout: MainLayout,
        exact: true,
        component: EmailMarketingView,
    },
    ...AuthRoutes,
    {
        path: '/',
        layout: DashboardLayout,
        routes: [
            ...AccountRoutes,
            ...AdCampaignsRoutes,
            ...AssistantPermissionsRoutes,
            ...BDCRoutes,
            ...CalendarsRoutes,
            ...CatalogRoutes,
            ...ChartsRoutes,
            ...ChatBotRoutes,
            ...ConversationsRoutes,
            ...DashboardsRoutes,
            ...DocumentsRoutes,
            ...DrilldownsRoutes,
            ...FinancialInstitutionsRoutes,
            ...GroupsRoutes,
            ...InvestmentChartRoutes,
            ...InternalReportsRoutes,
            ...LeadsRoutes,
            ...EvaluationsRoutes,
            ...MakesRoutes,
            ...MaintenancePackagesRoutes,
            ...ManagementRoutes,
            ...RecordingsRoutes,
            ...RecordingTestRoutes,
            ...ReportsRoutes,
            ...SalesRoutes,
            ...ServiceClientsRoutes,
            ...ContactsRoutes,
            ...ShowroomRoutes,
            ...SourcesRoutes,
            ...StatusRoutes,
            ...StoresRoutes,
            ...StoreGroupsRoutes,
            ...SubstatusRoutes,
            ...TemplatesTwilioRoutes,
            ...TicketsRoutes,
            ...UsersRoutes,
            ...UtilsRoutes,
            ...VehiclesRoutes,
            ...MetaAdsRoutes,
            ...ImpersonationRoutes,
            {
                exact: true,
                path: '/management',
                component: () => <Redirect to="/dashboards/general" />,
            },
            {
                exact: true,
                path: '/omnichannel',
                guard: RolesGuard,
                roles: ['rockstar'],
                component: ConversationsView,
            },
            {
                exact: true,
                path: '/ui',
                guard: RolesGuard,
                roles: ['rockstar'],
                component: lazy(() => import('src/views/ui/WarboxUI')),
            },
            {
                component: () => <Redirect to="/404" />,
            },
        ],
    },
    {
        path: '*',
        layout: MainLayout,
        routes: [
            {
                exact: true,
                path: '/',
                component: lazy(() => import('src/views/home/HomeView')),
            },
            {
                component: () => <Redirect to="/404" />,
            },
        ],
    },
];

export default routes;
